import React from 'react';
import { Button } from 'react-bootstrap';

import { useDiscord } from '../../../discord/useDiscord';
import { pickPlatformDisplayName } from '../../Game/dispatchTakeSeat';
import {
    OWNER_MARK_GLYPH,
    OWNER_MARK_LABEL,
    PLATFORM_AI,
    PLATFORM_TAG_META,
    platformForSeat,
} from '../identityMarks';

import './seat-row.css';

const DISCORD_AVATAR_URL = (discordUserId, discordAvatarHash) =>
    `https://cdn.discordapp.com/avatars/${discordUserId}/${discordAvatarHash}.png`;

const PLAYER_ONE_INDICATOR = '★';  // ★

// CC_LOBBY_OVERLAY_WINDOW (2026-06-07): COPY PLACEHOLDER — label for an
// empty seat in the read-only in-game roster. The pre-game lobby renders
// an OpenRow with a "Take this seat" button; in-game (read-only) the same
// slot is just an empty seat the viewer can see. Michael owns the wording.
const READ_ONLY_EMPTY_LABEL = 'Empty';

// CC_OWNER_FILL_EMPTY_SEAT_AI (2026-06-08): COPY PLACEHOLDER — the
// owner-only affordance to drop an AI onto a patiently-vacated (live)
// seat from the in-game read-only roster. Gated on `vacated` so it never
// shows on a never-filled placeholder (no GamePlayer to drive). Michael
// owns the wording.
const FILL_WITH_AI_LABEL = 'Fill with AI';


function OwnerMark() {
    // CC_SEAT_IDENTITY_MARKS: renders on the lobby owner's seat row so
    // everyone sees who owns the lobby. PLACEHOLDER glyph — see
    // identityMarks; same asset as the in-game menu mark.
    return (
        <span
            className="seat-row-owner-mark"
            title={OWNER_MARK_LABEL}
            aria-label={OWNER_MARK_LABEL}
        >
            {OWNER_MARK_GLYPH}
        </span>
    );
}


function PlatformTag({ platform }) {
    // CC_SEAT_IDENTITY_MARKS: one tag per seat — discord | web | ai.
    // PLACEHOLDER glyph + copy; see identityMarks for the heuristic and
    // the flags for Michael's art/copy pass.
    const meta = PLATFORM_TAG_META[platform];
    if (!meta) {
        return null;
    }
    return (
        <span
            className={`seat-row-platform-tag seat-row-platform-${platform}`}
            title={meta.label}
            aria-label={meta.label}
        >
            {meta.glyph}
        </span>
    );
}


function SeatLabel({ seatNumber }) {
    return <div className="seat-row-label">Seat {seatNumber + 1}</div>;
}


function PlayerOneButton({ isPlayerOne, isOwner, onDesignate }) {
    // CC_PLAYER_ONE_TRANSFER: enabled host-side on every occupied
    // row; click designates this seat as Player One (owner-local
    // state, sent on launch). Non-host viewers see a disabled
    // indicator only — designation is owner-only pre-launch.
    let label = isPlayerOne ? `${PLAYER_ONE_INDICATOR} Player 1` : 'Is Player 1';
    let title;
    if (isPlayerOne) {
        title = 'This seat is Player One.';
    } else if (isOwner) {
        title = 'Designate this seat as Player One.';
    } else {
        title = 'Only the host can designate Player One.';
    }
    let disabled = isPlayerOne || !isOwner;
    return (
        <Button
            variant={isPlayerOne ? 'hex' : 'secondary'}
            disabled={disabled}
            onClick={disabled ? undefined : onDesignate}
            className="seat-row-player-one"
            title={title}
        >
            {label}
        </Button>
    );
}


function OpenRow({
    seatNumber, isOwner, readOnly, vacated, onAddAI, onClose, onTakeSeat,
    onFillAI,
}) {
    if (readOnly) {
        // CC_LOBBY_OVERLAY_WINDOW: in-game read-only roster. An open seat
        // is just "Empty" to a viewer — no take/add-AI/close affordances.
        // CC_OWNER_FILL_EMPTY_SEAT_AI claims the reserved action slot: the
        // owner gets a Fill-with-AI button, but ONLY on a patiently-
        // vacated (live) seat — `vacated` from SeatSerializer
        // (CC_MIDGAME_SEAT_PRESENCE) gates out never-filled placeholders,
        // which have no GamePlayer for the AI to drive. Owner-only UX; the
        // server's owner-gate in replace_seat_with_ai is the authority
        // (one threat per layer). Non-owners and never-filled seats keep
        // the bare empty slot.
        return (
            <div className="seat-row seat-row-open seat-row-readonly">
                <SeatLabel seatNumber={seatNumber} />
                <div className="seat-row-occupant">
                    <div className="seat-row-empty-label">
                        {READ_ONLY_EMPTY_LABEL}
                    </div>
                </div>
                <div className="seat-row-controls">
                    {isOwner && vacated && (
                        <Button
                            variant="stem"
                            onClick={() => onFillAI && onFillAI(seatNumber)}
                            className="seat-row-action seat-row-action-fill-ai"
                            title="Fill this empty seat with an AI"
                        >
                            {FILL_WITH_AI_LABEL}
                        </Button>
                    )}
                </div>
            </div>
        );
    }
    return (
        <div className="seat-row seat-row-open">
            <SeatLabel seatNumber={seatNumber} />
            <div className="seat-row-controls">
                <Button
                    variant="honored"
                    onClick={() => onTakeSeat(seatNumber)}
                    className="seat-row-action seat-row-action-take"
                >
                    Take this seat
                </Button>
                {isOwner && (
                    <Button
                        variant="stem"
                        onClick={onAddAI}
                        className="seat-row-action seat-row-action-add-ai"
                    >
                        Add AI
                    </Button>
                )}
                {isOwner && (
                    <Button
                        variant="dread"
                        onClick={onClose}
                        className="seat-row-action seat-row-action-close"
                    >
                        Close
                    </Button>
                )}
            </div>
        </div>
    );
}


function HumanRow({
    seatNumber, occupant, isLocalUser, isOwner, isOwnerSeat, isPlayerOne,
    readOnly, onToggleReady, onDesignatePlayerOne, onKick,
}) {
    const discord = useDiscord();
    // CC_SEAT_IDENTITY_MARKS: per-seat platform tag. Heuristic source is
    // the captured Discord nick (occupant.platform_display_name) plus the
    // live embed flag for the local seat — see identityMarks.
    const platform = platformForSeat({
        platformDisplayName: occupant && occupant.platform_display_name,
        isLocalUser,
        isEmbedded: discord.isEmbedded,
    });
    let display;
    if (isLocalUser && discord.isEmbedded && discord.isReady
            && discord.discordUserId && discord.discordAvatarHash) {
        display = (
            <div className="seat-row-discord-identity">
                <img
                    src={DISCORD_AVATAR_URL(
                        discord.discordUserId, discord.discordAvatarHash)}
                    alt="discord-avatar"
                    className="seat-row-discord-avatar"
                />
                <div className="seat-row-discord-username">
                    {pickPlatformDisplayName(discord) || 'You'}
                </div>
            </div>
        );
    } else if (isLocalUser) {
        display = <div className="seat-row-player-name">You</div>;
    } else {
        // Seat-level display_name is the source of truth post-backend
        // rollout — it carries the per-seat captured Discord nick and
        // already encodes the full precedence chain on the server.
        // The user-level display_name fallback stays for resilience
        // during the rollout window and as a defense if a serializer
        // ever ships seat without the top-level field.
        let name = (occupant && occupant.display_name)
            || (occupant && occupant.user
                && occupant.user.display_name)
            || 'Player';
        display = <div className="seat-row-player-name">{name}</div>;
    }
    // CC_POSTGAME_ROSTER_FIXES C1: the patient-seat model keeps a
    // disconnected peer's row bound, so distinguish "still here" from
    // "dropped" visually. Only an explicit `connected === false` marks
    // disconnected — undefined/null (rollout window, AI/empty seats)
    // never trips it. Placeholder copy; Michael owns the final look.
    let isDisconnected = Boolean(occupant) && occupant.connected === false;
    let ready = Boolean(occupant && occupant.ready);
    let readyLabel = ready ? '✓ Ready' : 'Ready';
    let readyButton;
    if (isLocalUser && !readOnly) {
        readyButton = (
            <Button
                variant={ready ? 'seed' : 'deep'}
                onClick={onToggleReady}
                className="seat-row-ready-toggle"
            >
                {readyLabel}
            </Button>
        );
    } else {
        readyButton = (
            <div className="seat-row-ready-indicator">
                {ready ? '✓ Ready' : '⋯ Waiting'}
            </div>
        );
    }
    let rowClassName = 'seat-row seat-row-human';
    if (isDisconnected) {
        rowClassName += ' seat-row-disconnected';
    }
    return (
        <div className={rowClassName}>
            <SeatLabel seatNumber={seatNumber} />
            <div className="seat-row-occupant">
                {display}
                {isOwnerSeat && <OwnerMark />}
                <PlatformTag platform={platform} />
                {isDisconnected && (
                    <span
                        className="seat-row-conn-dot"
                        title="Disconnected"
                        aria-label="Disconnected"
                    >
                        ● Disconnected
                    </span>
                )}
            </div>
            <div className="seat-row-controls">
                {readyButton}
                {!readOnly && (
                    <PlayerOneButton
                        isPlayerOne={isPlayerOne}
                        isOwner={isOwner}
                        onDesignate={onDesignatePlayerOne}
                    />
                )}
                {/* CC_LOBBY_OVERLAY_WINDOW seam closed by
                  * CC_KICK_SEATS_AND_OBSERVERS: Player One stays pre-game
                  * only (!readOnly above), but Kick is owner-only on BOTH
                  * the pre-game lobby AND the in-game read-only roster —
                  * the owner kicks a playing seat from the overlay. The
                  * occupant goes patient; the kick wire is identical
                  * (gameApi.kickFromGroup). Still never the owner's own
                  * seat (!isLocalUser). */}
                {isOwner && !isLocalUser && (
                    <Button
                        variant="dread"
                        onClick={() => onKick && onKick(
                            occupant && occupant.user
                            && occupant.user.id)}
                        className="seat-row-action seat-row-action-kick"
                        title="Remove this player from the lobby"
                    >
                        Kick
                    </Button>
                )}
            </div>
        </div>
    );
}


function AIRow({
    seatNumber, isOwner, isPlayerOne, readOnly, onRemoveAI,
    onDesignatePlayerOne,
}) {
    return (
        <div className="seat-row seat-row-ai">
            <SeatLabel seatNumber={seatNumber} />
            <div className="seat-row-occupant">
                <div className="seat-row-player-name">AI</div>
                <PlatformTag platform={PLATFORM_AI} />
            </div>
            <div className="seat-row-controls">
                <div className="seat-row-ready-indicator">
                    {'✓ Ready'}
                </div>
                {!readOnly && isOwner && (
                    <Button
                        variant="dread"
                        onClick={onRemoveAI}
                        className="seat-row-action seat-row-action-remove-ai"
                    >
                        Remove AI
                    </Button>
                )}
                {!readOnly && (
                    <PlayerOneButton
                        isPlayerOne={isPlayerOne}
                        isOwner={isOwner}
                        onDesignate={onDesignatePlayerOne}
                    />
                )}
            </div>
        </div>
    );
}


function ClosedRow({ seatNumber, isOwner, readOnly, onReopen }) {
    return (
        <div className="seat-row seat-row-closed">
            <SeatLabel seatNumber={seatNumber} />
            <div className="seat-row-occupant">
                <div className="seat-row-closed-label">Closed</div>
            </div>
            <div className="seat-row-controls">
                {!readOnly && isOwner && (
                    <Button
                        variant="honored"
                        onClick={onReopen}
                        className="seat-row-action seat-row-action-reopen"
                    >
                        Re-open
                    </Button>
                )}
            </div>
        </div>
    );
}


export default function SeatRow(props) {
    const {
        seatNumber, seatState, occupant, isOwner, isOwnerSeat, isLocalUser,
        isPlayerOne, readOnly, vacated, onTakeSeat, onAddAI, onRemoveAI,
        onClose, onReopen, onToggleReady, onDesignatePlayerOne, onKick,
        onFillAI,
    } = props;
    if (seatState === 'open') {
        return (
            <OpenRow
                seatNumber={seatNumber}
                isOwner={isOwner}
                readOnly={readOnly}
                vacated={vacated}
                onAddAI={onAddAI}
                onClose={onClose}
                onTakeSeat={onTakeSeat}
                onFillAI={onFillAI}
            />
        );
    }
    if (seatState === 'human') {
        return (
            <HumanRow
                seatNumber={seatNumber}
                occupant={occupant}
                isLocalUser={isLocalUser}
                isOwner={isOwner}
                isOwnerSeat={isOwnerSeat}
                isPlayerOne={isPlayerOne}
                readOnly={readOnly}
                onToggleReady={onToggleReady}
                onDesignatePlayerOne={onDesignatePlayerOne}
                onKick={onKick}
            />
        );
    }
    if (seatState === 'ai') {
        return (
            <AIRow
                seatNumber={seatNumber}
                isOwner={isOwner}
                isPlayerOne={isPlayerOne}
                readOnly={readOnly}
                onRemoveAI={onRemoveAI}
                onDesignatePlayerOne={onDesignatePlayerOne}
            />
        );
    }
    return (
        <ClosedRow
            seatNumber={seatNumber}
            isOwner={isOwner}
            readOnly={readOnly}
            onReopen={onReopen}
        />
    );
}
